import {
  Box,
  CircularProgress,
  Fade,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ChevronLeft as ChevronLeftIcon } from '../../icons/chevron-left';
import { ChevronRight as ChevronRightIcon } from '../../icons/chevron-right';

interface Props {
  disabled: boolean;
  nextPage: () => void;
  prevPage: () => void;
  page: number;
  handlePerPageChange: (newPerPage: number) => void;
  perPage: number;
  prevDisabled: boolean;
  nextDisabled: boolean;
  totalPages: number;
  isFetching: boolean;
  changePage?: (newPage: number) => void;
  perPageOptions?: number[];
}

export const perPageDefaultOptions = [10, 20, 50, 100];

export const CompactPagination = ({
  disabled,
  totalPages,
  prevDisabled,
  nextDisabled,
  prevPage,
  nextPage,
  page,
  handlePerPageChange,
  isFetching,
  perPage,
  perPageOptions = perPageDefaultOptions,
}: Props) => {
  const { t } = useTranslation(['common']);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',

        width: '100%',
        padding: 2,
      }}>
      {totalPages > 0 ? (
        <Typography
          color="textSecondary"
          sx={{
            alignItems: 'center',
            display: 'flex',
          }}
          variant="body2"
          whiteSpace="nowrap">
          {t('common:page')}{' '}
          <Typography color="textPrimary" component="span" sx={{ mx: 1 }} variant="subtitle2">
            {page}
          </Typography>
          {t('common:of')}{' '}
          <Typography color="textPrimary" component="span" sx={{ ml: 1 }} variant="subtitle2">
            {totalPages}
          </Typography>
        </Typography>
      ) : (
        <Skeleton width={50} />
      )}
      <Typography
        color="textSecondary"
        sx={{
          ml: 2,
          alignItems: 'center',
          display: 'flex',
        }}
        variant="body2"
        whiteSpace="nowrap">
        {t('common:perPage')}
      </Typography>
      <Select
        size="small"
        value={perPage}
        sx={{ ml: 2 }}
        onChange={e => handlePerPageChange(+e.target.value)}>
        {perPageOptions.map(perPageValue => (
          <MenuItem value={perPageValue} key={perPageValue}>
            {perPageValue}
          </MenuItem>
        ))}
      </Select>
      <Box sx={{ flexGrow: 1 }} />
      <Fade in={isFetching} unmountOnExit>
        <CircularProgress size={20} />
      </Fade>
      <IconButton disabled={prevDisabled || disabled} onClick={prevPage}>
        <ChevronLeftIcon />
      </IconButton>
      <IconButton disabled={nextDisabled || disabled} onClick={nextPage}>
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
};
